/**
 * Stage Import API Service
 * Handles all API calls for stage/road import functionality
 */

import STAGE_IMPORT_CONFIG from '../../config/stageImportConfig';
import { API_config } from '../../config/apiConfig';

/**
 * Import a stage or road with GPX data
 * @param {FormData} formData - Form data containing all import parameters
 * @returns {Promise<Object>} API response
 */
export async function importStage(formData) {
    try {
        const response = await fetch(STAGE_IMPORT_CONFIG.apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': API_config.bearerAuthHeader
            },
            body: formData,
        });

        if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
                const errText = await response.text();
                const jsonMatch = errText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const errData = JSON.parse(jsonMatch[0]);
                    if (errData.error) errorMsg += ` - Server said: ${errData.error}`;
                } else if (errText) {
                    errorMsg += ` - Response: ${errText.substring(0, 100)}`;
                }
            } catch (e) { }
            throw new Error(errorMsg);
        }

        // The PHP API returns HTML, so we need to parse it
        const text = await response.text();

        // Try to extract JSON from the response if present
        // The PHP script might return JSON in <pre> tags or directly
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
            try {
                return { success: true, data: JSON.parse(jsonMatch[0]), rawResponse: text };
            } catch (e) {
                return { success: true, data: null, rawResponse: text };
            }
        }

        return { success: true, data: null, rawResponse: text };
    } catch (error) {
        console.error('Import error:', error);
        throw new Error(`Import failed: ${error.message}`);
    }
}

/**
 * Fetch list of recent stages and roads
 * @param {number} limit - Number of items to fetch
 * @returns {Promise<Array>} List of stages and roads
 */
export async function fetchStagesList(limit = STAGE_IMPORT_CONFIG.listLimit) {
    try {
        // Make a GET request to the PHP API
        const response = await fetch(STAGE_IMPORT_CONFIG.apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': API_config.bearerAuthHeader
            }
        });

        if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
                const errText = await response.text();
                const jsonMatch = errText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const errData = JSON.parse(jsonMatch[0]);
                    if (errData.error) errorMsg += ` - Server said: ${errData.error}`;
                } else if (errText) {
                    errorMsg += ` - Response: ${errText.substring(0, 100)}`;
                }
            } catch (e) { }
            throw new Error(errorMsg);
        }

        const data = await response.json();

        // The API returns data in format: { sections: [ { section: "LEG 1", os: [...], liaisons: [...] } ] }
        const items = [];

        if (data.sections && Array.isArray(data.sections)) {
            data.sections.forEach(sectionData => {
                const sectionName = sectionData.section;

                // Process OS (stages)
                if (sectionData.os && Array.isArray(sectionData.os)) {
                    sectionData.os.forEach(stage => {
                        items.push({
                            id: stage.id,
                            name: stage.name || '',
                            type: 'stage',
                            section: sectionName,
                            result_stage_id: stage.result_stage_id || null,
                            stage_typ: stage.stage_typ || null,
                            run_no: stage.run_no || null,
                            kind: 'OS',
                            template_road_id: stage.template_road_id || null,
                            length_m: stage.length_m || null,
                            distance_km: stage.distance_km || null,
                            created_at: stage.created_at || '',
                            updated_at: stage.updated_at || '',
                        });
                    });
                }

                // Process LIAISONS (dojazdówki)
                if (sectionData.liaisons && Array.isArray(sectionData.liaisons)) {
                    sectionData.liaisons.forEach(liaison => {
                        items.push({
                            id: liaison.id,
                            name: liaison.name || '',
                            type: 'stage',
                            section: sectionName,
                            result_stage_id: liaison.result_stage_id || null,
                            stage_typ: liaison.stage_typ || null,
                            run_no: liaison.run_no || null,
                            kind: 'LIAISON',
                            template_road_id: liaison.template_road_id || null,
                            length_m: liaison.length_m || null,
                            distance_km: liaison.distance_km || null,
                            created_at: liaison.created_at || '',
                            updated_at: liaison.updated_at || '',
                        });
                    });
                }
            });
        }

        // Also check for roads if they're in a separate array
        if (data.roads && Array.isArray(data.roads)) {
            data.roads.forEach(road => {
                items.push({
                    id: road.id,
                    name: road.name || '',
                    type: 'road',
                    section: null,
                    result_stage_id: null,
                    stage_typ: null,
                    run_no: null,
                    kind: null,
                    template_road_id: null,
                    length_m: road.length_m || null,
                    distance_km: road.distance_km || null,
                    created_at: road.created_at || '',
                    updated_at: road.updated_at || '',
                });
            });
        }

        // Sort by ID descending (newest first, assuming higher IDs are newer)
        items.sort((a, b) => b.id - a.id);

        return items.slice(0, limit);
    } catch (error) {
        console.error('Fetch stages list error:', error);
        // Return empty array instead of throwing to prevent UI errors
        return [];
    }
}

/**
 * Fetch list of available roads for selection
 * @returns {Promise<Array>} List of roads
 */
export async function fetchRoadsList() {
    try {
        // Fetch all items and filter for stages only
        const allItems = await fetchStagesList();
        const roads = allItems
            .filter(item => item.type === 'stage')
            .map(stage => ({
                id: stage.id,
                name: `${stage.section || 'Uncategorized'} - ${stage.name || 'Unnamed Stage'}`,
                distance_km: stage.distance_km,
                length_m: stage.length_m
            }));

        return roads;
    } catch (error) {
        console.error('Fetch roads list error:', error);
        return [];
    }
}

/**
 * Delete a stage by ID
 * @param {number} id - Stage ID
 * @returns {Promise<Object>} API response
 */
export async function deleteStage(id) {
    try {
        const formData = new FormData();
        formData.append('action', 'delete');
        formData.append('id', id);
        formData.append('type', 'stage');

        const response = await fetch(STAGE_IMPORT_CONFIG.apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': API_config.bearerAuthHeader
            },
            body: formData,
        });

        if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
                const errText = await response.text();
                const jsonMatch = errText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const errData = JSON.parse(jsonMatch[0]);
                    if (errData.error) errorMsg += ` - Server said: ${errData.error}`;
                } else if (errText) {
                    errorMsg += ` - Response: ${errText.substring(0, 100)}`;
                }
            } catch (e) { }
            throw new Error(errorMsg);
        }

        const text = await response.text();
        return { success: true, message: 'Stage deleted successfully', rawResponse: text };
    } catch (error) {
        console.error('Delete stage error:', error);
        throw new Error(`Failed to delete stage: ${error.message}`);
    }
}

/**
 * Update a stage or road
 * @param {number} id - Stage/Road ID
 * @param {string} type - 'stage' or 'road'
 * @param {Object} data - fields to update (name, section)
 * @returns {Promise<Object>} API response
 */
export async function updateStage(id, type, data) {
    try {
        const formData = new FormData();
        formData.append('action', 'update');
        formData.append('id', id);
        formData.append('type', type);

        if (data.name) formData.append('name', data.name);
        if (data.section) formData.append('section', data.section);

        const response = await fetch(STAGE_IMPORT_CONFIG.apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': API_config.bearerAuthHeader
            },
            body: formData,
        });

        if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
                const errText = await response.text();
                const jsonMatch = errText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const errData = JSON.parse(jsonMatch[0]);
                    if (errData.error) errorMsg += ` - Server said: ${errData.error}`;
                } else if (errText) {
                    errorMsg += ` - Response: ${errText.substring(0, 100)}`;
                }
            } catch (e) { }
            throw new Error(errorMsg);
        }

        const text = await response.text();
        return { success: true, message: 'Update successful', rawResponse: text };
    } catch (error) {
        console.error('Update error:', error);
        throw new Error(`Failed to update: ${error.message}`);
    }
}

/**
 * Delete a road by ID
 * @param {number} id - Road ID
 * @returns {Promise<Object>} API response
 */
export async function deleteRoad(id) {
    try {
        const formData = new FormData();
        formData.append('action', 'delete');
        formData.append('id', id);
        formData.append('type', 'road');

        const response = await fetch(STAGE_IMPORT_CONFIG.apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': API_config.bearerAuthHeader
            },
            body: formData,
        });

        if (!response.ok) {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
                const errText = await response.text();
                const jsonMatch = errText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const errData = JSON.parse(jsonMatch[0]);
                    if (errData.error) errorMsg += ` - Server said: ${errData.error}`;
                } else if (errText) {
                    errorMsg += ` - Response: ${errText.substring(0, 100)}`;
                }
            } catch (e) { }
            throw new Error(errorMsg);
        }

        const text = await response.text();
        return { success: true, message: 'Road deleted successfully', rawResponse: text };
    } catch (error) {
        console.error('Delete road error:', error);
        throw new Error(`Failed to delete road: ${error.message}`);
    }
}

/**
 * Build FormData for stage/road import
 * @param {Object} params - Import parameters
 * @returns {FormData} Prepared form data
 */
export function buildImportFormData(params) {
    const formData = new FormData();

    // Required fields
    formData.append('stage_name', params.stageName || '');
    formData.append('thin_step', params.thinStep || STAGE_IMPORT_CONFIG.defaultThinStep);
    formData.append('stage_type', params.stageType || 'stage');

    // Optional fields
    if (params.section) {
        formData.append('section', params.section);
    }

    if (params.kind) {
        formData.append('kind', params.kind);
    }

    if (params.stageTyp) {
        formData.append('stage_typ', params.stageTyp);
    }

    if (params.runNo) {
        formData.append('run_no', params.runNo);
    }

    if (params.resultStageId) {
        formData.append('result_stage_id', params.resultStageId);
    }

    if (params.storeXml) {
        formData.append('store_xml', '1');
    }

    // GPX source mode
    formData.append('gpx_mode', params.gpxMode || 'upload');

    if (params.gpxMode === 'use_road' && params.useRoadId) {
        formData.append('use_road_id', params.useRoadId);
    }

    // File upload
    if (params.gpxFile) {
        formData.append('gpx_file', params.gpxFile);
    }

    return formData;
}
